import { create } from 'zustand';

/**
 * Per-user audio preferences (mic/speaker/ringer device, gain, echo, etc).
 * Persisted in localStorage so settings survive across sessions.
 */
export interface AudioPrefs {
  /** 'default' = system default; otherwise MediaDeviceInfo.deviceId */
  micDeviceId: string;
  speakerDeviceId: string;
  ringerDeviceId: string;
  /** 0–100 */
  micGain: number;
  ringerVolume: number;
  /**
   * Echo cancellation. KAPATMA except when using a real headset where
   * speaker→mic feedback is physically impossible. With speakers/laptop mic
   * leaving this off causes the FAR side to hear their own echo.
   */
  echoCancellation: boolean;
  /** Browser noise suppression — agresif olduğu için default OFF (sesi boğar). */
  noiseSuppression: boolean;
  /**
   * Auto gain control. Default ON. Kapalı bırakırsak karşı taraf bizi "uzaktan
   * duyuyor gibi" — laptop dahili mic'ler düşük raw seviyede capture eder ve
   * 8kHz PCMA down-sampling sonrası daha da kısılır. Twilio/AWS Connect/Five9
   * hepsi default ON kullanır. Headset kullanıcısı ister kapatabilir.
   */
  autoGainControl: boolean;
}

const STORAGE_KEY = 'ccark.audio';

const DEFAULTS: AudioPrefs = {
  micDeviceId: 'default',
  speakerDeviceId: 'default',
  ringerDeviceId: 'default',
  micGain: 100,
  ringerVolume: 100,
  echoCancellation: true,   // safe default — prevent far-side echo
  noiseSuppression: false,  // default off — keeps voice natural
  autoGainControl: true,    // safe default — far side hears agent at consistent level
};

function load(): AudioPrefs {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

interface AudioState extends AudioPrefs {
  update: (patch: Partial<AudioPrefs>) => void;
}

export const useAudio = create<AudioState>((set, get) => ({
  ...load(),
  update: (patch) => {
    set(patch);
    try {
      const { micDeviceId, speakerDeviceId, ringerDeviceId, micGain, ringerVolume, echoCancellation, noiseSuppression, autoGainControl } = get();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        micDeviceId, speakerDeviceId, ringerDeviceId, micGain, ringerVolume, echoCancellation, noiseSuppression, autoGainControl,
      } satisfies AudioPrefs));
    } catch { /* */ }
  },
}));

/** getUserMedia constraints derived from current preferences.
 *
 *  sampleRate / channelCount eski kodda hardcoded'tu — bazı Windows Intel
 *  Smart Sound sürücüleri bu kısıtlamalarla uyumsuz çalışıp SİLENT track
 *  döndürüyor. Browser'a default sample rate'i bırakıyoruz; Opus 48kHz,
 *  G.711 8kHz olarak browser otomatik resample eder.
 */
export function micConstraints(): MediaTrackConstraints {
  const p = useAudio.getState();
  const c: MediaTrackConstraints = {
    echoCancellation: p.echoCancellation,
    noiseSuppression: p.noiseSuppression,
    autoGainControl: p.autoGainControl,
  };
  if (p.micDeviceId && p.micDeviceId !== 'default') {
    c.deviceId = { ideal: p.micDeviceId };
  }
  console.log('[mic] constraints', c);
  return c;
}
